import { css } from '@emotion/react'
import { useAllContactsList, usePendingTrustedContactInvitationCount } from 'api/goodtrust/contact'
import { useDashboard } from 'api/goodtrust/user/dashboard'
import {
  contactNotificationFilters,
  postNotificationsRead,
  useNotifications,
} from 'api/goodtrust/user/notification'
import { Block } from 'components/Block'
import { ContactAddModal } from 'components/contact/contactAddModal/ContactAddModal'
import { ContactAllList } from 'components/contact/contactAllList/ContactAllList'
import {
  ContactInvitedModal,
  ContactInvitedModalConfig,
} from 'components/contact/contactInvitedModal/ContactInvitedModal'
import { ContactBoundModal } from 'components/contact/contactNotificationModal/ContactBoundModal'
import { ContactNotificationModal } from 'components/contact/contactNotificationModal/ContactNotificationModal'
import { ContactPromoteDemoteModal } from 'components/contact/contactNotificationModal/ContactPromoteDemoteModal'
import { ContactUnboundModal } from 'components/contact/contactNotificationModal/ContactUnboundModal'
import { HeaderDashboard } from 'components/header/headerDashboard/HeaderDashboard'
import { Icon } from 'components/icon'
import { useModal } from 'components/modal/Modal'
import {
  OnboardingModal,
  TOnboardingModalSteps,
} from 'components/modal/onboardingModal/OnboardingModal'
import { PageTitle } from 'components/PageTitle'
import { Picture } from 'components/picture/Picture'
import { ProfileContent, ProfileWrapper } from 'components/profile/profileContent/ProfileContent'
import { ProfileSidebar } from 'components/profile/profileSidebar/ProfileSidebar'
import { SideLayout } from 'components/sideLayout/SideLayout'
import { Spacer } from 'components/spacer/Spacer'
import { WizardCardBig, WizardCardBigGrid3 } from 'components/wizard/wizardCardItem/WizardCardItem'
import { WizardHeadingWithImage } from 'components/wizard/wizardHeadingWithImage/WizardHeadingWithImage'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { fireEvent } from 'utils/analytics'
import { colors } from 'utils/colors'
import { unwrapResponse } from 'utils/fetcher'
import { isNonNullable, logError } from 'utils/general'
import { useRunOnceReady } from 'utils/hooks'
import { getIntlProps } from 'utils/i18n/getIntlProps'
import { pxToRem } from 'utils/styled'
import { ApiType } from 'utils/types'

export default function Page() {
  const { t } = useTranslation('trusted_contact')
  const [isLegacy, setIsLegacy] = useState(false)
  const [contact, setContact] = useState<ApiType['TrustedContactResponse']>()
  const dashboard = useDashboard()

  const ONBOARDING_STEPS: TOnboardingModalSteps[] = [
    {
      illustration: {
        imageKey: 'illustrations/deputy',
      },
      title: t('trusted_contact.onboarding.page_1.title'),
      description: t('trusted_contact.onboarding.page_1.desc'),
    },
    {
      illustration: { imageKey: 'illustrations/selectPersonWoman' },
      title: t('trusted_contact.onboarding.page_2.title'),
      description: t('trusted_contact.onboarding.page_2.desc'),
    },
    {
      illustration: { imageKey: 'illustrations/death' },
      title: t('trusted_contact.onboarding.page_3.title'),
      description: t('trusted_contact.onboarding.page_3.desc'),
    },
    {
      illustration: { imageKey: 'illustrations/selectPersonWoman' },
      title: dashboard.data?.json?.has_legacy_contact
        ? t('trusted_contact.onboarding.page_4.title_with_legacy_title')
        : t('trusted_contact.onboarding.page_4.title_no_legacy_title'),
      description: dashboard.data?.json?.has_legacy_contact
        ? t('trusted_contact.onboarding.page_4.title_with_legacy_desc')
        : t('trusted_contact.onboarding.page_4.title_no_legacy_desc'),
      buttonLabel: dashboard.data?.json?.has_legacy_contact
        ? t('trusted_contact.onboarding.page_4.action_label_with_legacy')
        : t('trusted_contact.onboarding.page_4.action_label_no_legacy'),
    },
  ]

  const myContacts = useAllContactsList()
  const { data: notifications, mutate: mutateNotifications } = useNotifications(
    contactNotificationFilters
  )
  const { mutate: mutateNotificationCount } = usePendingTrustedContactInvitationCount()

  const [invitedModal, showInvitedModal] = useModal(
    ({ close, isOpen }) =>
      isOpen ? <ContactInvitedModal close={close} contact={contact} /> : null,
    ContactInvitedModalConfig
  )

  const [addContactModal, showAddContactModal] = useModal(
    ({ isOpen, close }) =>
      isOpen ? (
        <ContactAddModal
          isLegacy={isLegacy}
          onSuccess={async (contact) => {
            setContact(contact)
            await myContacts.mutate()
            showInvitedModal()
            close()
          }}
        />
      ) : null,
    {
      modalCss: css`
        max-width: ${pxToRem(580)}rem;
      `,
    }
  )

  const [notificationsModal, showNotificationsModal] = useModal(
    ({ close, isOpen }) =>
      isOpen ? (
        <ContactNotificationModal
          close={close}
          notificationContents={notifications?.json?.map((n) => {
            switch (n.type) {
              case 'ACCEPTED_TRUSTED_CONTACT':
                return (
                  <ContactBoundModal
                    contact={n.opposite_user}
                    contactKind="trusted-contact-user"
                    type={n.opposite_user?.type}
                  />
                )
              case 'REMOVED_AS_TRUSTED_CONTACT':
                return (
                  <ContactUnboundModal
                    contact={n.opposite_user}
                    contactKind="trusted-contact-user"
                  />
                )
              default:
                return (
                  <ContactPromoteDemoteModal
                    contact={n.opposite_user}
                    contactKind="trusted-contact-user"
                    isPromoted={n.type === 'PROMOTED_TO_LEGACY_CONTACT'}
                  />
                )
            }
          })}
        />
      ) : null,
    {
      modalCss: css`
        max-width: ${pxToRem(480)}rem;
      `,
      onClose: async () => {
        try {
          const ids = notifications?.json?.map((n) => n.uuid).filter(isNonNullable)
          if (!ids?.length || ids.some((i) => !i)) throw Error('Missing notification ID')
          await postNotificationsRead(ids).then(unwrapResponse)
          await mutateNotificationCount()
          await mutateNotifications()
        } catch (err) {
          logError(err)
        }
      },
    }
  )

  const [onboardModal, openOnboardModal] = useModal(
    ({ close, isOpen }) =>
      isOpen ? (
        <OnboardingModal
          title={t('trusted_contact.onboarding.title')}
          steps={ONBOARDING_STEPS}
          onNextClick={() => {
            fireEvent('tc_read_more_action', { action: 'next' })
          }}
          onSuccess={() => {
            fireEvent('tc_read_more_action', { action: 'finish' })
            setIsLegacy(!dashboard.data?.json?.has_legacy_contact)
            void showAddContactModal()
            close(true)
          }}
        />
      ) : null,
    {
      modalCss: css`
        max-width: 30rem;
      `,
      onClose: () => {
        fireEvent('tc_read_more_action', { action: 'close' })
      },
    }
  )

  useRunOnceReady(!!notifications?.json?.length, async () => {
    showNotificationsModal()
  })

  return (
    <SideLayout left={<ProfileSidebar />} header={<HeaderDashboard />}>
      {addContactModal}
      {invitedModal}
      {notificationsModal}
      {onboardModal}

      <PageTitle title={t('trusted_contact.meta_title')} />

      <Block>
        <ProfileWrapper>
          <ProfileContent hideBack>
            <WizardHeadingWithImage
              title={t('trusted_contact.title')}
              subtitle={t('trusted_contact.subtitle')}
              description={t('trusted_contact.desc')}
              image={<Picture imageKey={'illustrations/deputy'} sizes={'200px'} />}
              textColumnCss={{ maxWidth: '45rem' }}
            />

            <WizardCardBigGrid3
              css={css`
                & svg,
                path {
                  stroke-width: 1px;
                }
              `}
            >
              <WizardCardBig
                title={t('trusted_contact.legacy.title')}
                description={t('trusted_contact.legacy.desc')}
                icon={<Icon name="ShieldCheck" color={colors.gray[500]} />}
                duration={t('trusted_contact.legacy.duration')}
                actionLabel={t('trusted_contact.legacy.action_label')}
                onClick={() => {
                  setIsLegacy(true)
                  fireEvent('tc_add_contact_clicked', { type: 'LEGACY_CONTACT' })
                  showAddContactModal()
                }}
                data-testid="device-computer-password-add"
              />
              <WizardCardBig
                title={t('trusted_contact.trusted.title')}
                description={t('trusted_contact.trusted.desc')}
                icon={<Icon name="PersonAdd" color={colors.gray[500]} />}
                duration={t('trusted_contact.trusted.duration')}
                actionLabel={t('trusted_contact.trusted.action_label')}
                onClick={() => {
                  setIsLegacy(false)
                  fireEvent('tc_add_contact_clicked', { type: 'STANDARD' })
                  showAddContactModal()
                }}
                data-testid="device-computer-password-add"
              />
              <WizardCardBig
                title={t('trusted_contact.how_sharing_work.title')}
                description={t('trusted_contact.how_sharing_work.desc')}
                icon={<Icon name="InfoCircle" color={colors.gray[500]} />}
                duration={t('trusted_contact.how_sharing_work.duration')}
                actionLabel={t('trusted_contact.how_sharing_work.action_label')}
                onClick={() => {
                  fireEvent('tc_read_more_open')
                  void openOnboardModal()
                }}
                data-testid="device-computer-password-add"
              />
            </WizardCardBigGrid3>

            <Spacer size={[2, 4]} />

            <ContactAllList title={t('trusted_contact.contact_all_list.title')} />
          </ProfileContent>
        </ProfileWrapper>
      </Block>
    </SideLayout>
  )
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  return { props: { ...(await getIntlProps(ctx, ['trusted_contact', 'dashboard'], true)) } }
}
